import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';

/* ─── Micro icon helpers ──────────────────────────────────── */
const Ic = ({ size = 15, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IcEdit = () => <Ic><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Ic>;
const IcTrash = () => <Ic><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></Ic>;
const IcFolders = () => <Ic><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/><polyline points="16 13 12 17 8 13"/><line x1="12" y1="17" x2="12" y2="9"/></Ic>;
const IcSearch = () => <Ic><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></Ic>;
const IcClose = () => <Ic size={16}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ic>;
const IcPlus = () => <Ic size={15}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Ic>;

/* ─── Helpers ─────────────────────────────────────────────── */
const toSlug = (name) =>
  name.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const EMPTY_FORM = { name: '', slug: '', icon: '', description: '', display_order: 0 };

/* ─── Skeleton loader ─────────────────────────────────────── */
const Skeleton = () => (
  <div className="divide-y divide-slate-100">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-4 px-6 py-4">
        <div className="h-3 w-6 bg-slate-200 rounded animate-pulse" />
        <div className="h-9 w-9 bg-slate-200 rounded-lg animate-pulse shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-44 bg-slate-200 rounded animate-pulse" />
          <div className="h-2.5 w-28 bg-slate-100 rounded animate-pulse" />
        </div>
        <div className="h-5 w-16 bg-slate-100 rounded-full animate-pulse" />
        <div className="h-6 w-20 bg-slate-100 rounded animate-pulse" />
      </div>
    ))}
  </div>
);

/* ─── Empty state ─────────────────────────────────────────── */
const EmptyState = ({ onAdd, hasFilter }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-6">
    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-100 mb-4">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-slate-400">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
      </svg>
    </div>
    <p className="text-[14px] font-semibold text-slate-700">
      {hasFilter ? 'No categories match your search' : 'No categories yet'}
    </p>
    <p className="text-[13px] text-slate-400 mt-1 max-w-xs">
      {hasFilter ? 'Try adjusting your search or filter.' : 'Create your first category to get started.'}
    </p>
    {!hasFilter && (
      <button onClick={onAdd} className="mt-5 flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-[13px] font-medium rounded-lg hover:bg-blue-700 transition-colors">
        <IcPlus /> New Category
      </button>
    )}
  </div>
);

/* ─── Main component ──────────────────────────────────────── */
const CategoryManagement = ({ onSelectCategory }) => {
  const { accessToken } = useAuth();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingForm, setSavingForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugEdited, setSlugEdited] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const flash = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  /* ── Data ── */
  useEffect(() => {
    if (accessToken) load();
  }, [accessToken]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('https://vmc-backend-vt2w.onrender.com/api/categories/admin/all', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to load categories');
      const { data } = await res.json();
      setCategories(data);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Panel ── */
  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingCat(null);
    setSlugEdited(false);
    setErrors({});
    setTouched({});
    setShowPanel(true);
  };
  const openEdit = (cat) => {
    setForm({ name: cat.name, slug: cat.slug, icon: cat.icon || '', description: cat.description || '', display_order: cat.display_order ?? 0 });
    setEditingCat(cat);
    setSlugEdited(true);
    setErrors({});
    setTouched({});
    setShowPanel(true);
  };
  const closePanel = () => {
    setShowPanel(false);
    setEditingCat(null);
    setForm(EMPTY_FORM);
    setSlugEdited(false);
    setErrors({});
    setTouched({});
  };

  /* ── Validation ── */
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value || value.trim().length === 0) return 'Name is required';
        if (value.trim().length < 3) return 'Name must be at least 3 characters';
        if (value.length > 100) return 'Name must be 100 characters or less';
        return '';
      case 'slug':
        if (!value || value.trim().length === 0) return 'Slug is required';
        if (value.length < 3) return 'Slug must be at least 3 characters';
        if (value.length > 100) return 'Slug must be 100 characters or less';
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) return 'Use only lowercase letters, numbers, and hyphens (e.g., my-category)';
        return '';
      case 'icon':
        if (value && value.length > 50) return 'Icon must be 50 characters or less';
        return '';
      case 'display_order':
        if (value !== '' && Number(value) < 0) return 'Display order must be 0 or greater';
        return '';
      case 'description':
        if (value && value.length > 500) return `Description is too long (${value.length}/500 characters max)`;
        return '';
      default:
        return '';
    }
  };

  const validateAll = () => {
    const allTouched = { name: true, slug: true, icon: true, display_order: true, description: true };
    const newErrors = {};
    Object.keys(allTouched).forEach((field) => {
      const e = validateField(field, form[field]);
      if (e) newErrors[field] = e;
    });
    setTouched(allTouched);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (name) => {
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, form[name]) }));
  };

  const err = (field) => (touched[field] ? errors[field] || '' : '');

  /* ── Form ── */
  const onNameChange = (v) => {
    const slug = slugEdited ? form.slug : toSlug(v);
    setForm((f) => ({ ...f, name: v, slug }));
    setErrors((prev) => ({
      ...prev,
      name: touched.name || v.length > 0 ? validateField('name', v) : '',
      ...(!slugEdited && { slug: validateField('slug', slug) }),
    }));
  };

  const onSlugChange = (v) => {
    setForm((f) => ({ ...f, slug: v }));
    setSlugEdited(true);
    setErrors((prev) => ({ ...prev, slug: validateField('slug', v) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAll()) return;
    setSavingForm(true);
    try {
      const isEdit = !!editingCat;
      const res = await fetch(
        isEdit ? `https://vmc-backend-vt2w.onrender.com/api/categories/${editingCat.id}` : 'https://vmc-backend-vt2w.onrender.com/api/categories/create',
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) throw new Error('Failed to save category');
      await load();
      closePanel();
      flash('success', isEdit ? 'Category updated.' : 'Category created.');
    } catch (e) {
      flash('error', e.message);
    } finally {
      setSavingForm(false);
    }
  };

  /* ── Delete ── */
  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      const res = await fetch(`https://vmc-backend-vt2w.onrender.com/api/categories/${confirmDel.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to delete category');
      await load();
      flash('success', `"${confirmDel.name}" deleted.`);
    } catch (e) {
      flash('error', e.message);
    } finally {
      setConfirmDel(null);
    }
  };

  /* ── Derived ── */
  const filtered = categories.filter((c) => {
    const q = searchQ.toLowerCase();
    const textOk = c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
    const statusOk = filterStatus === 'all' || (filterStatus === 'active' ? c.is_active : !c.is_active);
    return textOk && statusOk;
  });

  const stats = {
    total: categories.length,
    active: categories.filter((c) => c.is_active).length,
    inactive: categories.filter((c) => !c.is_active).length,
  };

  const hasFilter = !!searchQ || filterStatus !== 'all';

  /* ── Render ── */
  return (
    <>
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[70] flex items-center gap-2.5 rounded-xl px-4 py-3 text-[13px] font-medium border shadow-lg shadow-black/10 transition-all
          ${toast.type === 'success' ? 'bg-white border-emerald-200 text-emerald-700' : 'bg-white border-red-200 text-red-600'}`}>
          {toast.type === 'success'
            ? <svg className="shrink-0 text-emerald-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            : <svg className="shrink-0 text-red-500" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          }
          {toast.msg}
        </div>
      )}

      {/* Main card */}
      <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-200">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <span className="text-[13px] text-slate-500">
              <span className="font-semibold text-slate-800">{stats.total}</span> total
            </span>
            <span className="w-px h-4 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {stats.active} active
            </span>
            <span className="w-px h-4 bg-slate-200" />
            <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
              {stats.inactive} inactive
            </span>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                <IcSearch />
              </span>
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search categories…"
                className="pl-8 pr-3 py-1.5 w-52 text-[13px] text-slate-700 placeholder-slate-400 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-2.5 py-1.5 text-[13px] text-slate-700 bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
            >
              <option value="all">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <button
              onClick={openNew}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 text-white text-[13px] font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <IcPlus /> New Category
            </button>
          </div>
        </div>

        {/* Table / States */}
        {loading ? (
          <Skeleton />
        ) : filtered.length === 0 ? (
          <EmptyState onAdd={openNew} hasFilter={hasFilter} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70">
                    <th className="pl-6 pr-2 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-10">#</th>
                    <th className="px-3 py-3 w-14" />
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Category</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 hidden lg:table-cell">Description</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                    <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 hidden md:table-cell">Order</th>
                    <th className="pl-3 pr-6 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((cat, i) => (
                    <tr key={cat.id} className="group hover:bg-slate-50/60 transition-colors">
                      <td className="pl-6 pr-2 py-3.5 text-[11px] font-mono text-slate-400">{i + 1}</td>
                      <td className="px-3 py-3.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-lg leading-none select-none">
                          {cat.icon || '📁'}
                        </div>
                      </td>
                      <td className="px-3 py-3.5">
                        <p className="font-semibold text-slate-800 leading-snug">{cat.name}</p>
                        <p className="text-[11.5px] font-mono text-slate-400 mt-0.5">{cat.slug}</p>
                      </td>
                      <td className="px-3 py-3.5 hidden lg:table-cell max-w-[240px]">
                        <p className="text-slate-500 truncate">{cat.description || <span className="text-slate-300">—</span>}</p>
                      </td>
                      <td className="px-3 py-3.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-medium
                          ${cat.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${cat.is_active ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                          {cat.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-3.5 text-slate-400 tabular-nums hidden md:table-cell">{cat.display_order ?? '—'}</td>
                      <td className="pl-3 pr-6 py-3.5">
                        <div className="flex items-center justify-end gap-0.5">
                          <button
                            onClick={() => onSelectCategory(cat.id, cat.name)}
                            title="Manage subcategories"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <IcFolders />
                          </button>
                          <button
                            onClick={() => openEdit(cat)}
                            title="Edit category"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                          >
                            <IcEdit />
                          </button>
                          <button
                            onClick={() => setConfirmDel({ id: cat.id, name: cat.name })}
                            title="Delete category"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <IcTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40">
              <p className="text-[12px] text-slate-400">
                Showing <span className="font-medium text-slate-600">{filtered.length}</span> of{' '}
                <span className="font-medium text-slate-600">{categories.length}</span> categories
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Form drawer ───────────────────────────────── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-200
          ${showPanel ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={closePanel}
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-[480px] max-w-full bg-white flex flex-col shadow-2xl shadow-black/15
          transition-transform duration-[240ms] ease-[cubic-bezier(0.4,0,0.2,1)]
          ${showPanel ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Panel header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-200 shrink-0">
          <div>
            <p className="text-[15px] font-semibold text-slate-800">
              {editingCat ? 'Edit Category' : 'New Category'}
            </p>
            <p className="text-[12.5px] text-slate-500 mt-0.5">
              {editingCat ? 'Make changes to this category.' : 'Fill in the details to create a new category.'}
            </p>
          </div>
          <button
            onClick={closePanel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors mt-0.5"
          >
            <IcClose />
          </button>
        </div>

        {/* Form (wraps scrollable body + sticky footer) */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">

            {/* Name */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                onBlur={() => handleBlur('name')}
                placeholder="e.g., Restaurants & Bars"
                className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg focus:outline-none focus:ring-1 transition-colors
                  ${err('name') ? 'border-red-400 focus:border-red-400 focus:ring-red-300 bg-red-50/30' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {err('name') ? (
                <p className="mt-1 text-[11.5px] text-red-500 flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {err('name')}
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-slate-400">{form.name.length}/100 characters</p>
              )}
            </div>

            {/* Slug */}
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Slug <span className="text-red-500">*</span>
              </label>
              <div className={`flex items-center border rounded-lg overflow-hidden transition-colors
                ${err('slug') ? 'border-red-400 bg-red-50/30 focus-within:border-red-400 focus-within:ring-1 focus-within:ring-red-300' : 'border-slate-200 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500'}`}>
                <span className="px-3 py-2 text-[12px] text-slate-400 bg-slate-50 border-r border-slate-200 font-mono select-none shrink-0 leading-5">
                  /
                </span>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => onSlugChange(e.target.value)}
                  onBlur={() => handleBlur('slug')}
                  placeholder="restaurants-bars"
                  className="flex-1 px-3 py-2 text-[13px] text-slate-800 font-mono placeholder-slate-400 bg-transparent focus:outline-none"
                />
              </div>
              {err('slug') ? (
                <p className="mt-1 text-[11.5px] text-red-500 flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {err('slug')}
                </p>
              ) : (
                <p className="text-[11.5px] text-slate-400 mt-1">Auto-generated from name. Used in URLs.</p>
              )}
            </div>

            {/* Icon + Order */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Icon (emoji)</label>
                <input
                  type="text"
                  value={form.icon}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, icon: e.target.value }));
                    setErrors((prev) => ({ ...prev, icon: validateField('icon', e.target.value) }));
                  }}
                  onBlur={() => handleBlur('icon')}
                  placeholder="🍽️"
                  className={`w-full px-3 py-2 text-[20px] border rounded-lg focus:outline-none focus:ring-1 transition-colors
                    ${err('icon') ? 'border-red-400 focus:border-red-400 focus:ring-red-300 bg-red-50/30' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'}`}
                />
                {err('icon') ? (
                  <p className="mt-1 text-[11.5px] text-red-500 flex items-center gap-1">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {err('icon')}
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-400">Optional. Paste an emoji.</p>
                )}
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.display_order}
                  onChange={(e) => {
                    const num = parseInt(e.target.value);
                    const val = isNaN(num) ? 0 : num;
                    setForm((f) => ({ ...f, display_order: val }));
                    setErrors((prev) => ({ ...prev, display_order: validateField('display_order', val) }));
                  }}
                  onBlur={() => handleBlur('display_order')}
                  className={`w-full px-3 py-2 text-[13px] text-slate-800 border rounded-lg focus:outline-none focus:ring-1 transition-colors
                    ${err('display_order') ? 'border-red-400 focus:border-red-400 focus:ring-red-300 bg-red-50/30' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'}`}
                />
                {err('display_order') ? (
                  <p className="mt-1 text-[11.5px] text-red-500 flex items-center gap-1">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {err('display_order')}
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-400">Lower numbers appear first.</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[12px] font-semibold text-slate-700">Description</label>
                <span className={`text-[11px] tabular-nums ${
                  form.description.length > 500 ? 'text-red-500 font-medium' :
                  form.description.length > 450 ? 'text-amber-500 font-medium' : 'text-slate-400'
                }`}>
                  {form.description.length}/500
                </span>
              </div>
              <textarea
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }));
                  setErrors((prev) => ({ ...prev, description: validateField('description', e.target.value) }));
                }}
                onBlur={() => handleBlur('description')}
                rows={3}
                placeholder="Brief description of this category…"
                className={`w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border rounded-lg resize-none focus:outline-none focus:ring-1 transition-colors
                  ${err('description') ? 'border-red-400 focus:border-red-400 focus:ring-red-300 bg-red-50/30' : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500'}`}
              />
              {err('description') && (
                <p className="mt-1 text-[11.5px] text-red-500 flex items-center gap-1">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {err('description')}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex items-center justify-end gap-2.5 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={closePanel}
              className="px-4 py-2 text-[13px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={savingForm}
              className="px-5 py-2 text-[13px] font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {savingForm ? 'Saving…' : editingCat ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </aside>

      {/* ── Delete confirm modal ──────────────────────── */}
      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/25 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xl shadow-black/10 p-6 w-full max-w-[360px]">
            <div className="flex items-start gap-4 mb-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 mt-0.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <div>
                <p className="font-semibold text-slate-800">Delete category</p>
                <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-slate-700">"{confirmDel.name}"</span>?
                  This will also remove all its subcategories and cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2.5">
              <button
                onClick={() => setConfirmDel(null)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={doDelete}
                className="px-4 py-2 text-[13px] font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryManagement;